import {
  AdmPwdUpdateRequestDto,
  AdmUpdateRequestDto,
  AdmDetailResponseDto,
} from "@/dto/AdmDto";
import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { findById, deleteById, update, changePassword } from "@/apis/admin";
import { useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { useModal } from "mui-modal-provider"; // 모달 컴포넌트
import { ErrorDto } from "@/dto/ErrorDto";
import { Confirm, Alert } from "@/organism";
export default function useDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [admInfo, setAdmInfo] = useState<AdmDetailResponseDto | null>(null);
  const { showModal } = useModal();

  const fetchAdmData = useCallback(
    async (id: string) => {
      try {
        setIsLoading(true);
        const data = await findById(id);
        setAdmInfo(data);
      } catch (e) {
        console.error(e);
        navigate("/admins");
      } finally {
        setIsLoading(false);
      }
    },
    [navigate]
  );

  useEffect(() => {
    if (id) {
      fetchAdmData(id);
    }
  }, [id, fetchAdmData]);

  /**
   * 관리자 정보 수정 이벤트 핸들러
   */
  const handleAdmUpdate = async (adm: AdmUpdateRequestDto, id: string) => {
    const modal = showModal(Confirm, {
      title: "관리자 정보 수정",
      content: "관리자 정보를 수정하시겠습니까?",
      onOk: async () => {
        modal.destroy();
        try {
          setIsLoading(true);
          const result = await update(adm, id);
          if (result.success) {
            enqueueSnackbar(result.message || "관리자 정보가 수정되었습니다.", {
              variant: "success",
            });
            if (id) {
              await fetchAdmData(id);
            }
          }
        } catch (e) {
          const error = e as ErrorDto;
          showModal(Alert, {
            title: "오류",
            content:
              error.details?.errorMessage || "관리자 정보 수정에 실패했습니다.",
            onClose: () => {},
          });
        } finally {
          setIsLoading(false);
        }
      },
      onCancel: () => {
        modal.destroy();
      },
    });
  };

  /**
   * 관리자 비밀번호 수정 이벤트 핸들러
   */
  const handleAdmPwdUpdate = async (
    id: string,
    admPwd: AdmPwdUpdateRequestDto
  ) => {
    try {
      setIsLoading(true);
      const result = await changePassword(id, admPwd);
      if (result.success) {
        enqueueSnackbar(
          "비밀번호가 변경되었습니다. 해당 매니저에게 알려주세요.",
          {
            variant: "success",
          }
        );
        navigate("/admins");
      }
    } catch (e) {
      const error = e as ErrorDto;
      enqueueSnackbar(
        error.details?.errorMessage || "비밀번호 변경에 실패했습니다.",
        {
          variant: "error",
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 관리자 삭제 이벤트 핸들러
   */
  const handleAdmDelete = async (id: string) => {
    const modal = showModal(Confirm, {
      title: "관리자 삭제",
      content: "관리자를 삭제하시겠습니까?",
      onOk: async () => {
        modal.destroy();
        try {
          setIsLoading(true);
          const result = await deleteById(id);
          if (result.success) {
            enqueueSnackbar(result.message || "관리자가 삭제되었습니다.", {
              variant: "success",
            });
            navigate("/admins");
          }
        } catch (error) {
          const e = error as ErrorDto;
          showModal(Alert, {
            title: "오류",
            content: e.details?.errorMessage || "관리자 삭제에 실패했습니다.",
            onClose: () => {},
          });
        } finally {
          setIsLoading(false);
        }
      },
      onCancel: () => {
        modal.destroy();
      },
    });
  };

  return {
    isLoading,
    handleAdmUpdate,
    handleAdmPwdUpdate,
    handleAdmDelete,
    admInfo,
  };
}
